/**
 * Quick-Capture-Modal fuer ein neues persoenliches Todo.
 *
 * Wird sowohl vom Header-Plus-Button (PortalShell) als auch von der TodosPage
 * geoeffnet. Einzelne Quelle der Wahrheit fuer Datum-Pickling und Submit-Logik.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Calendar, UserPlus, Bell } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { GuardedModal, UserPicker, useDialogs } from '@onvice/portal-ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;  // wird nach erfolgreichem Insert gerufen — Caller kann Liste reloaden
}

/**
 * Dokument-Navigation (Abhakliste 2.94, 2026-10-01): Dialog-Schutz über GuardedModal — Klick
 * daneben, X und Escape fragen nach, sobald etwas eingegeben ist; Pflichtfeld und Fehler
 * als Meldung.
 */
export default function TodoQuickCaptureModal({ open, onClose, onCreated }: Props) {
  const dlg = useDialogs();
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [assigneeLabel, setAssigneeLabel] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setDueAt('');
      setReminderAt('');
      setAssigneeId(null);
      setAssigneeLabel('');
      // Auto-Focus nach Modal-Mount.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const dirty = title.trim().length > 0 || dueAt !== '' || reminderAt !== '' || assigneeId != null;

  const submit = async (): Promise<void> => {
    const t = title.trim();
    if (!t) {
      await dlg.notify({ kind: 'error', title: 'Todo kann nicht angelegt werden', message: 'Bitte zuerst ergänzen:', items: ['Titel'] });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/todos', {
        title: t,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        reminder_at: reminderAt ? new Date(reminderAt).toISOString() : null,
        assignee_user_id: assigneeId,
      });
      onCreated?.();
      onClose();
    } catch (err) {
      await dlg.error('Todo wurde nicht angelegt', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <GuardedModal
      open
      title="Neues Todo"
      label="Todo"
      dirty={dirty}
      busy={submitting}
      onClose={onClose}
      size="sm"
      testAnchor="todo-new"
      footer={(requestClose) => (
        <>
          <button
            type="button"
            onClick={requestClose}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            data-action="create"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px]"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Anlegen
          </button>
        </>
      )}
    >
        <div className="space-y-4">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder="Was ist zu tun?"
            data-test-anchor="todo-title"
            className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <Calendar size={14} /> Fällig (optional)
            </label>
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <Bell size={14} /> Erinnerung (optional)
            </label>
            <input
              type="datetime-local"
              value={reminderAt}
              onChange={(e) => setReminderAt(e.target.value)}
              className="w-full h-11 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1.5">
              <UserPlus size={14} /> Zuweisen an (optional)
            </label>
            <UserPicker
              value={assigneeId}
              label={assigneeLabel}
              onChange={(id, name) => { setAssigneeId(id); setAssigneeLabel(name); }}
            />
          </div>
        </div>
    </GuardedModal>
  );
}
